<template>
  <div ref="wrapperRef" class="m-daterange-wrapper">
    <div class="m-daterange-container">
      <!-- First Date Input -->
      <div class="m-daterange-part">
        <span class="m-daterange-bound" aria-hidden="true">{{ t('ui.from') }}</span>
        <FloatLabel variant="on" class="m-daterange-part-control">
          <DatePicker appendTo="#ui-popup-root" :inputId="uniqueIdDateFrom" v-model="fromDate" :disabled="readonly || disabled"
                      :class="['m-daterange-input', 'm-daterange-from']" :dateFormat="dateFormat" :maxDate="maxDate"
                      :minDate="minDate" showIcon iconDisplay="input" showButtonBar
                      :todayButtonProps="todayButtonProps" :clearButtonProps="clearButtonProps"
                      :aria-label="t('ui.from')"
                      @click="(event) => handleRangePickerClick(event, 'from')"
                      @update:modelValue="handleFromDateChange" @blur="handleBlur">
            <template #inputicon="{ clickCallback }">
              <span class="pi pi-calendar" @click="clickCallback" />
            </template>
          </DatePicker>
        </FloatLabel>
      </div>

      <!-- Separator Icon (desktop) -->
      <div class="m-daterange-separator" aria-hidden="true">
        <i class="pi pi-minus" />
      </div>

      <!-- Second Date Input -->
      <div class="m-daterange-part">
        <span class="m-daterange-bound" aria-hidden="true">{{ t('ui.to') }}</span>
        <FloatLabel variant="on" class="m-daterange-part-control">
          <DatePicker appendTo="#ui-popup-root" :inputId="uniqueIdDateTo" v-model="toDate" :disabled="readonly || disabled"
            :class="['m-daterange-input', 'm-daterange-to']" :dateFormat="dateFormat" :maxDate="maxDate"
            :minDate="fromDate || minDate" showIcon iconDisplay="input" showButtonBar
            :todayButtonProps="todayButtonProps" :clearButtonProps="clearButtonProps"
            :aria-label="t('ui.to')"
            @click="(event) => handleRangePickerClick(event, 'to')"
            @update:modelValue="handleToDateChange" @blur="handleBlur">
            <template #inputicon="{ clickCallback }">
              <span class="pi pi-calendar" @click="clickCallback" />
            </template>
          </DatePicker>
        </FloatLabel>
      </div>
    </div>

    <div v-if="hint" class="m-daterange-hint">
      {{ hint }}
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  resolveDateOnApplyCurrentDateClick,
  resolvePickerPropsFromPlatformFormat,
} from '@/common/utils/applyCurrentDateOnClick'
import DatePicker from 'primevue/datepicker'
import { computed, ref, useAttrs, useId } from 'vue'
import { useI18n } from '@/common/composables/useI18n'

interface Props {
  modelValue?: { from?: Date | string | null; to?: Date | string | null } | null
  label?: string
  fromPlaceholder?: string
  toPlaceholder?: string
  hint?: string
  readonly?: boolean
  disabled?: boolean
  minDate?: Date
  maxDate?: Date
  dateFormat?: string
  applyCurrentDateOnClick?: boolean
  platformFormat?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  label: '',
  fromPlaceholder: '',
  toPlaceholder: '',
  hint: '',
  readonly: false,
  disabled: false,
  minDate: undefined,
  maxDate: undefined,
  dateFormat: 'dd.mm.yy',
  applyCurrentDateOnClick: false,
  platformFormat: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: { from?: Date | null; to?: Date | null } | null]
  'change': [value: { from?: Date | null; to?: Date | null } | null]
  'blur': []
  focus: []
}>()

const { t } = useI18n()
const attrs = useAttrs()
const uniqueIdDateFrom = useId()
const uniqueIdDateTo = useId()
const wrapperRef = ref<HTMLElement | null>(null)

const filteredProps = computed(() => {
  const {
    modelValue,
    label,
    hint,
    fromPlaceholder,
    toPlaceholder,
    applyCurrentDateOnClick: _apply,
    platformFormat: _platformFormat,
    ...rest
  } = props
  return { ...rest, ...attrs }
})

const pickerProps = computed(() =>
  resolvePickerPropsFromPlatformFormat(props.platformFormat || 'dd.MM.yyyy'),
)

function isApplyCurrentDateClickTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(
    target.closest('.p-datepicker-input') ||
      target.closest('.p-datepicker-input-icon-container') ||
      target.closest('.p-datepicker-dropdown'),
  )
}

function readRangeInputText(part: 'from' | 'to'): string {
  const selector = part === 'from' ? '.m-daterange-from' : '.m-daterange-to'
  const root = wrapperRef.value?.querySelector(selector)
  const input = root?.querySelector('.p-datepicker-input') as HTMLInputElement | null
  return input?.value ?? ''
}

function handleRangePickerClick(event: MouseEvent, part: 'from' | 'to') {
  if (!props.applyCurrentDateOnClick || props.readonly || props.disabled) return
  if (event.button !== 0) return
  if (!isApplyCurrentDateClickTarget(event.target)) return

  const selector = part === 'from' ? '.m-daterange-from' : '.m-daterange-to'
  const root = wrapperRef.value?.querySelector(selector)
  if (!root?.contains(event.target as Node)) return

  const modelPart = part === 'from' ? props.modelValue?.from : props.modelValue?.to
  const resolved = resolveDateOnApplyCurrentDateClick({
    modelValue: modelPart ?? null,
    inputText: readRangeInputText(part),
    platformFormat: props.platformFormat ?? 'dd.MM.yyyy',
    pickerProps: pickerProps.value,
  })
  if (!resolved) return

  emit('focus')
  if (part === 'from') {
    fromDate.value = resolved
    emit('change', { from: resolved, to: toDate.value })
  } else {
    toDate.value = resolved
    emit('change', { from: fromDate.value, to: resolved })
  }
}

// Parse the model value
const fromDate = computed({
  get: () => {
    if (!props.modelValue?.from) return null
    if (typeof props.modelValue.from === 'string') {
      // Try to parse various date formats
      const date = new Date(props.modelValue.from)
      return isNaN(date.getTime()) ? null : date
    }
    return props.modelValue.from
  },
  set: (value) => {
    emit('update:modelValue', {
      from: value as Date | null,
      to: (props.modelValue?.to as Date | null) || null
    })
  }
})

const toDate = computed({
  get: () => {
    if (!props.modelValue?.to) return null
    if (typeof props.modelValue.to === 'string') {
      // Try to parse various date formats
      const date = new Date(props.modelValue.to)
      return isNaN(date.getTime()) ? null : date
    }
    return props.modelValue.to
  },
  set: (value) => {
    emit('update:modelValue', {
      from: (props.modelValue?.from as Date | null) || null,
      to: value as Date | null
    })
  }
})

// Event handlers
const handleFromDateChange = (value: Date | null) => {
  fromDate.value = value
  emit('change', { from: value, to: toDate.value })
}

const handleToDateChange = (value: Date | null) => {
  toDate.value = value
  emit('change', { from: fromDate.value, to: value })
}

// Устанавливаем минимальную дату как 1950-01-01 если не задана
const minDate = computed(() => props.minDate || new Date(1950, 0, 1))
const maxDate = computed(() => props.maxDate)

const todayButtonProps = computed(() => ({
  label: t('ui.today'),
  severity: 'secondary',
  text: false,
  size: 'small',
  class: 'mx-auto text-700 border-round px-4 py-2 font-medium shadow-none'
}))

const clearButtonProps = {
  class: 'hidden',
}

const handleBlur = () => {
  emit('blur')
}
</script>

<style scoped lang="scss">
.m-daterange-wrapper {
  position: relative;
  width: auto;
  max-width: 100%;
}

.m-daterange-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: auto;
  max-width: 100%;
}

.m-daterange-part {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 auto;
  min-width: 0;
}

.m-daterange-part-control {
  flex: 0 0 auto;
  min-width: 0;
  width: auto;
}

.m-daterange-bound {
  display: none;
  flex-shrink: 0;
  min-width: 1.25rem;
  color: var(--app-text-muted, #6b7280);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1;
}

.m-daterange-input {
  flex: 0 0 auto;

  :deep(.p-datepicker) {
    width: auto;
    min-width: 0;
    position: relative;

    &.p-disabled {
      opacity: 1 !important;
    }

    .p-inputtext {
      min-height: 40px;
      padding: 0.5rem 2rem 0.5rem 0.75rem;
      font-size: 14px;
      width: 150px;
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;
      background: #ffffff !important;
      background-color: #ffffff !important;
      border: 1px solid #d1d5db !important;
      border-radius: 4px !important;
      color: #374151 !important;

      &::placeholder {
        color: rgba(0, 0, 0, 0.38);
      }

      &:focus {
        border-color: var(--primary-color, #1e3a6e) !important;
        box-shadow: none !important;
        background: #ffffff !important;
        background-color: #ffffff !important;
      }

      &:disabled,
      &[readonly] {
        opacity: 1 !important;
        color: #4b5563 !important;
        -webkit-text-fill-color: #4b5563;
        font-size: 14px;
        background: #f9fafb !important;
        background-color: #f9fafb !important;
      }
    }

    .p-datepicker-dropdown {
      display: none;
    }

    .p-datepicker-input-icon-container {
      position: absolute;
      top: 50%;
      right: 0.5rem;
      margin-block-start: 0;
      transform: translateY(-50%);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.25rem;
      height: 1.25rem;
      color: #6b7280;

      .pi {
        cursor: pointer;
        font-size: 0.95rem;
      }
    }

    &.p-inputwrapper:focus-within {
      .p-inputtext {
        border-color: var(--primary-color, #1e3a6e) !important;
        box-shadow: none !important;
      }
    }

    .p-datepicker-panel {
      background-color: #ffffff !important;
      border: 1px solid #d1d5db !important;

      .p-datepicker-header {
        padding: 0.5rem;
        background-color: #ffffff !important;
      }

      .p-datepicker-calendar {

        th,
        td {
          padding: 0.25rem;

          >span {
            width: 2rem;
            height: 2rem;
            background-color: #ffffff !important;
            color: #374151 !important;

            &:hover {
              background-color: #f3f4f6 !important;
            }

            &.p-highlight {
              background-color: #e5e7eb !important;
            }
          }
        }
      }
    }
  }
}

.m-daterange-separator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  height: 23px;
  color: #6b7280;
  flex-shrink: 0;

  i {
    font-size: 12px;
  }
}

.m-daterange-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
  padding: 0 0.5rem;
}

// Responsive design
@media (max-width: 768px) {
  .m-daterange-wrapper {
    width: 100%;
  }

  .m-daterange-container {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    width: 100%;
  }

  .m-daterange-separator {
    display: none;
  }

  .m-daterange-bound {
    display: inline-block;
  }

  .m-daterange-part {
    flex: 1 1 auto;
    width: 100%;
  }

  .m-daterange-part-control {
    flex: 1 1 auto;
    width: 100%;
  }

  .m-daterange-input {
    flex: 1 1 auto;
    width: 100% !important;
    max-width: none !important;
  }

  :deep(.p-datepicker) {
    width: 100%;
  }

  :deep(.p-datepicker .p-inputtext) {
    width: 100% !important;
    max-width: none !important;
  }
}
</style>
